import React from 'react';
import { Link } from 'react-router';
import { useStyletron } from 'baseui';
import classnames from 'classnames';
import moment from 'moment';
import { Enums } from '@letsdothis/tracking';
import {
  HighFive as HighFiveIcon,
  Comment as CommentIcon,
  Check as CheckIcon,
} from '@letsdothis/lds/lib/experimental/Icon';

import { useTracking } from '@/tracking';
import Icon, { Icons } from '@shared/Icons/Icon';
import { SimpleNotificationAction, NotificationActionKind } from '@/types/user';

import * as styles from './styles.module.css';

type Props = {
  timestamp?: string | undefined;
  action: SimpleNotificationAction;
  onClick: () => void;
};

const Notification = ({ action, timestamp, onClick }: Props): JSX.Element => {
  const { componentClicked } = useTracking();
  const [css, theme] = useStyletron();
  const {
    message,
    actor,
    relativePermalink,
    permalink,
    thumbnail,
    kind,
    type: notificationType,
  } = action;
  const NotificationKindIcon =
    kind &&
    {
      [NotificationActionKind.LIKE]: HighFiveIcon,
      [NotificationActionKind.COMMENT]: CommentIcon,
      [NotificationActionKind.APPROVED]: CheckIcon,
    }[kind];
  const notificationKindColor =
    kind &&
    {
      [NotificationActionKind.LIKE]: theme.colors.primary,
      [NotificationActionKind.COMMENT]: theme.colors.primary,
      [NotificationActionKind.APPROVED]: theme.colors.positive,
    }[kind];
  const link = relativePermalink || permalink;
  const onNotificationTrackingClick = () => {
    onClick();
    componentClicked({
      short_name: `${notificationType?.toLowerCase()}_notification`,
      component_type: Enums.ComponentClickedComponentType.Link,
      component_link: link,
    });
  };

  return (
    <Link
      to={link || '#'}
      onClick={onNotificationTrackingClick}
      className={styles.linkWrapper}
    >
      <div className={styles.notification}>
        <div className={styles.column}>
          {actor ? (
            <div className={styles.avatar}>
              <span className={styles.initials}>
                {actor?.givenName?.[0] || ' '}
                {actor?.familyName?.[0] || ' '}
              </span>
            </div>
          ) : (
            <Icon className={styles.staff} icon={Icons.UNDERSCORE.DEFAULT} />
          )}
        </div>

        <div className={classnames(styles.column, styles.textContainer)}>
          <span>{message}</span>
          <div className={styles.timeContainer}>
            {NotificationKindIcon && (
              <NotificationKindIcon
                size={16}
                className={css({ marginRight: theme.sizing.scale200 })}
                color={notificationKindColor}
              />
            )}
            <span>
              {moment(parseInt(timestamp || '0', 10)).toNow(true)} ago
            </span>
          </div>
        </div>
        {thumbnail && (
          <div className={styles.column}>
            <img
              className={styles.thumbnail}
              alt={thumbnail.alt}
              src={thumbnail.uri}
            />
          </div>
        )}
      </div>
    </Link>
  );
};

export default Notification;
